// frontend/src/pages/AdminPanel.jsx - UPDATED VERSION
import React, { useState, useEffect } from 'react';
import internationalTransferService from '../services/internationalTransferService';
import accountService from '../services/accountService';
import Card from '../components/common/Card';

const AdminPanel = () => {
  const [activeTab, setActiveTab] = useState('accounts');
  const [accounts, setAccounts] = useState([]);
  const [pendingTransfers, setPendingTransfers] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [generatedCodes, setGeneratedCodes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [accountsData, transfersData] = await Promise.all([
        accountService.getAccounts(),
        internationalTransferService.adminGetPendingTransfers(),
      ]);
      setAccounts(accountsData);
      setPendingTransfers(transfersData);
    } catch (error) {
      setError('Failed to load admin data. Please ensure you have admin privileges.');
      console.error('Error fetching admin data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateFeatures = async (accountId, features) => {
    try {
      setLoading(true);
      setError('');
      await internationalTransferService.adminUpdateAccountFeatures(accountId, features);
      setSuccess('Account features updated successfully!');
      fetchData();
      setSelectedAccount(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update account features.');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateCodes = async (transferId) => {
    try {
      setLoading(true);
      setError('');
      const result = await internationalTransferService.adminGenerateCodes(transferId);
      
      // Fetch transfer details to get the generated codes
      const transfers = await internationalTransferService.adminGetPendingTransfers();
      const updatedTransfer = transfers.find(t => t.id === transferId);
      
      if (updatedTransfer) {
        setGeneratedCodes({
          transfer_id: transferId,
          codes: [
            { step_number: 1, code: updatedTransfer.admin_code_1 },
            { step_number: 2, code: updatedTransfer.admin_code_2 },
            { step_number: 3, code: updatedTransfer.admin_code_3 },
            { step_number: 4, code: updatedTransfer.admin_code_4 },
            { step_number: 5, code: updatedTransfer.admin_code_5 },
          ]
        });
      }
      
      setSuccess('Verification codes generated successfully!');
      fetchData();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate verification codes.');
    } finally {
      setLoading(false);
    }
  };

  const formatStatus = (status) => {
    const statusMap = {
      'INITIATED': 'Initiated',
      'AWAITING_CODE_1': 'Awaiting Code 1',
      'AWAITING_CODE_2': 'Awaiting Code 2',
      'AWAITING_CODE_3': 'Awaiting Code 3',
      'AWAITING_CODE_4': 'Awaiting Code 4',
      'AWAITING_CODE_5': 'Awaiting Code 5',
      'COMPLETED': 'Completed',
      'FAILED': 'Failed',
      'CANCELLED': 'Cancelled',
    };
    return statusMap[status] || status;
  };

  const EditAccountModal = ({ account, onClose, onSave }) => {
    const [features, setFeatures] = useState({
      daily_transfer_limit: account.features?.daily_transfer_limit || 10000,
      international_transfer_enabled: account.features?.international_transfer_enabled || true,
      requires_verification: account.features?.requires_verification || true,
      max_beneficiaries: account.features?.max_beneficiaries || 10,
      notes: account.features?.notes || '',
    });

    const handleSubmit = async (e) => {
      e.preventDefault();
      await onSave(account.id, features);
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4 max-h-screen overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-neutral-900">Edit Account Features</h3>
            <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mb-6 p-4 bg-neutral-50 rounded-xl">
            <p className="text-sm text-neutral-600">Account: <span className="font-semibold">{account.account_number}</span></p>
            <p className="text-sm text-neutral-600">Owner: <span className="font-semibold">{account.username}</span></p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-2">
                Daily Transfer Limit ($)
              </label>
              <input
                type="number"
                value={features.daily_transfer_limit}
                onChange={(e) => setFeatures({ ...features, daily_transfer_limit: parseFloat(e.target.value) })}
                min="0"
                step="0.01"
                className="w-full px-4 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-2">
                Max Beneficiaries
              </label>
              <input
                type="number"
                value={features.max_beneficiaries}
                onChange={(e) => setFeatures({ ...features, max_beneficiaries: parseInt(e.target.value) })}
                min="0"
                className="w-full px-4 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="intl_enabled"
                checked={features.international_transfer_enabled}
                onChange={(e) => setFeatures({ ...features, international_transfer_enabled: e.target.checked })}
                className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
              />
              <label htmlFor="intl_enabled" className="text-sm font-semibold text-neutral-700">
                Enable International Transfers
              </label>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="requires_verification"
                checked={features.requires_verification}
                onChange={(e) => setFeatures({ ...features, requires_verification: e.target.checked })}
                className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
              />
              <label htmlFor="requires_verification" className="text-sm font-semibold text-neutral-700">
                Require Verification
              </label>
            </div>

            <div>
              <label className="block text-sm font-semibold text-neutral-700 mb-2">
                Admin Notes
              </label>
              <textarea
                value={features.notes}
                onChange={(e) => setFeatures({ ...features, notes: e.target.value })}
                rows="3"
                className="w-full px-4 py-3 border border-neutral-200 rounded-xl focus:ring-2 focus:ring-primary-500"
              />
            </div>

            <div className="flex space-x-4 pt-4">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 bg-primary-600 text-white py-3 rounded-xl hover:bg-primary-700 transition font-semibold disabled:opacity-50"
              >
                Save Changes
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-neutral-200 text-neutral-700 py-3 rounded-xl hover:bg-neutral-300 transition font-semibold"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const TransferCodesModal = ({ transfer, codes, onClose }) => {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-2xl p-8 max-w-2xl w-full mx-4 max-h-screen overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-2xl font-bold text-neutral-900">Verification Codes</h3>
            <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mb-6 p-4 bg-neutral-50 rounded-xl">
            <p className="text-sm text-neutral-600">Transfer Reference: <span className="font-semibold">{transfer.reference_number}</span></p>
            <p className="text-sm text-neutral-600">Amount: <span className="font-semibold">${transfer.amount}</span></p>
            <p className="text-sm text-neutral-600">Recipient: <span className="font-semibold">{transfer.to_account_name}</span></p>
          </div>

          <div className="space-y-4">
            <h4 className="font-semibold text-neutral-900 mb-4">5-Step Verification Codes:</h4>
            
            {codes.map((codeData) => (
              <div key={codeData.step_number} className="p-4 bg-neutral-50 rounded-xl">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-neutral-900">Step {codeData.step_number}</span>
                  <span className="text-sm text-neutral-600">Provide to customer</span>
                </div>
                <div className="text-center py-3">
                  <code className="text-3xl font-mono font-bold text-primary-700 tracking-widest">
                    {codeData.code || 'Not generated'}
                  </code>
                </div>
                <p className="text-xs text-neutral-500 text-center mt-2">
                  Give this code to the account owner when they reach step {codeData.step_number}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-xl">
            <h4 className="font-semibold text-yellow-900 mb-2">Important Instructions:</h4>
            <ul className="text-sm text-yellow-700 space-y-1">
              <li>• Only provide one code at a time</li>
              <li>• Wait for customer to verify each code before giving the next</li>
              <li>• Codes expire when transfer is completed or cancelled</li>
              <li>• Keep codes confidential</li>
            </ul>
          </div>

          <div className="mt-6">
            <button
              onClick={onClose}
              className="w-full bg-neutral-200 text-neutral-700 py-3 rounded-xl hover:bg-neutral-300 transition font-semibold"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-neutral-900 mb-2">Admin Panel</h1>
          <p className="text-neutral-600">Manage accounts and international transfers</p>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-6 py-4 rounded-xl mb-6">
            {success}
          </div>
        )}

        {/* Tabs */}
        <div className="flex space-x-4 mb-6">
          <button
            onClick={() => setActiveTab('accounts')}
            className={`px-6 py-3 rounded-xl font-semibold transition ${
              activeTab === 'accounts'
                ? 'bg-primary-600 text-white'
                : 'bg-white text-neutral-600 hover:bg-neutral-100'
            }`}
          >
            Account Management
          </button>
          <button
            onClick={() => setActiveTab('transfers')}
            className={`px-6 py-3 rounded-xl font-semibold transition ${
              activeTab === 'transfers'
                ? 'bg-primary-600 text-white'
                : 'bg-white text-neutral-600 hover:bg-neutral-100'
            }`}
          >
            Pending Transfers ({pendingTransfers.length})
          </button>
        </div>

        {/* Accounts Tab */}
        {activeTab === 'accounts' && (
          <Card className="bg-white rounded-2xl shadow-lg border border-neutral-100 p-6">
            <h2 className="text-2xl font-bold text-neutral-900 mb-6">User Accounts</h2>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-neutral-200">
                    <th className="text-left py-3 px-4 font-semibold text-neutral-700">Account</th>
                    <th className="text-left py-3 px-4 font-semibold text-neutral-700">Owner</th>
                    <th className="text-left py-3 px-4 font-semibold text-neutral-700">Balance</th>
                    <th className="text-left py-3 px-4 font-semibold text-neutral-700">Daily Limit</th>
                    <th className="text-left py-3 px-4 font-semibold text-neutral-700">Int'l Transfer</th>
                    <th className="text-left py-3 px-4 font-semibold text-neutral-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {accounts.map((account) => (
                    <tr key={account.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-semibold text-neutral-900">{account.account_number}</p>
                          <p className="text-sm text-neutral-600">{account.account_type}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-semibold text-neutral-900">{account.username}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4 font-semibold text-neutral-900">
                        ${parseFloat(account.balance).toFixed(2)}
                      </td>
                      <td className="py-3 px-4">
                        ${account.features?.daily_transfer_limit?.toFixed(2) || '10000.00'}
                      </td>
                      <td className="py-3 px-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                          account.features?.international_transfer_enabled
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {account.features?.international_transfer_enabled ? 'Enabled' : 'Disabled'}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={() => setSelectedAccount(account)}
                          className="text-primary-600 hover:text-primary-700 font-semibold"
                        >
                          Edit Features
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Transfers Tab */}
        {activeTab === 'transfers' && (
          <div className="space-y-6">
            {pendingTransfers.length === 0 ? (
              <Card className="bg-white rounded-2xl shadow-lg border border-neutral-100 p-12 text-center">
                <p className="text-neutral-500 text-lg">No pending international transfers</p>
              </Card>
            ) : (
              pendingTransfers.map((transfer) => (
                <Card key={transfer.id} className="bg-white rounded-2xl shadow-lg border border-neutral-100 p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-xl font-bold text-neutral-900 mb-4">Transfer Details</h3>
                      <div className="space-y-2">
                        <p className="text-sm"><span className="font-semibold">Reference:</span> {transfer.reference_number}</p>
                        <p className="text-sm"><span className="font-semibold">From:</span> {transfer.from_account?.user?.username} ({transfer.from_account_number})</p>
                        <p className="text-sm"><span className="font-semibold">To:</span> {transfer.to_account_name}</p>
                        <p className="text-sm"><span className="font-semibold">Bank:</span> {transfer.to_bank_name}, {transfer.to_bank_country}</p>
                        <p className="text-sm"><span className="font-semibold">Amount:</span> ${transfer.amount}</p>
                        <p className="text-sm">
                          <span className="font-semibold">Status:</span>{' '}
                          <span className={`px-2 py-1 rounded text-xs font-semibold ${
                            transfer.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                            transfer.status === 'FAILED' || transfer.status === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {formatStatus(transfer.status)}
                          </span>
                        </p>
                        <p className="text-sm"><span className="font-semibold">Current Step:</span> {transfer.current_step} of 5</p>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-xl font-bold text-neutral-900 mb-4">Actions</h3>
                      <div className="space-y-3">
                        <button
                          onClick={() => {
                            handleGenerateCodes(transfer.id);
                            setSelectedTransfer(transfer);
                          }}
                          disabled={loading}
                          className="w-full bg-primary-600 text-white py-3 rounded-xl hover:bg-primary-700 transition font-semibold disabled:opacity-50"
                        >
                          Generate/Show Verification Codes
                        </button>
                      </div>

                      {/* Display generated codes */}
                      {generatedCodes && generatedCodes.transfer_id === transfer.id && (
                        <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-xl">
                          <h4 className="font-bold text-green-900 mb-3">Verification Codes:</h4>
                          <div className="space-y-2">
                            {generatedCodes.codes.map((codeData) => (
                              <div key={codeData.step_number} className="flex justify-between items-center bg-white p-3 rounded-lg">
                                <span className="font-semibold">Code {codeData.step_number}:</span>
                                <span className="font-mono text-lg text-green-700">{codeData.code}</span>
                              </div>
                            ))}
                          </div>
                          <p className="text-xs text-green-700 mt-3">
                            Provide these codes to the account owner one at a time.
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Edit Account Modal */}
        {selectedAccount && (
          <EditAccountModal
            account={selectedAccount}
            onClose={() => setSelectedAccount(null)}
            onSave={handleUpdateFeatures}
          />
        )}

        {/* Transfer Codes Modal */}
        {selectedTransfer && generatedCodes && (
          <TransferCodesModal
            transfer={selectedTransfer}
            codes={generatedCodes.codes}
            onClose={() => {
              setSelectedTransfer(null);
              setGeneratedCodes(null);
            }}
          />
        )}
      </div>
    </div>
  );
};

export default AdminPanel;